import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { euro, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Btn, Modal, Field, inputCls, EmptyState, Toolbar } from "@/components/kit";
import { Plus, Trash2, Boxes, Pencil, AlertTriangle } from "lucide-react";

const EMPTY = { name: "", sku: "", category: "", description: "", price: 0, cost: 0, quantity: 0, reorder_level: 5, unit: "unité" };

export default function Inventory() {
  const { user } = useAuth();
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/products").then((r) => setList(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(EMPTY); setEditId(null); setOpen(true); };
  const openEdit = (p) => { setForm({ ...EMPTY, ...p }); setEditId(p.id); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = { ...form, price: Number(form.price), cost: Number(form.cost), quantity: Number(form.quantity), reorder_level: Number(form.reorder_level) };
    try {
      if (editId) await api.put(`/products/${editId}`, payload);
      else await api.post("/products", payload);
      toast.success(editId ? "Produit mis à jour" : "Produit ajouté");
      setOpen(false); load();
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Supprimer ce produit ?")) return;
    try { await api.delete(`/products/${id}`); toast.success("Supprimé"); load(); }
    catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
  };

  return (
    <div className="animate-fade-up" data-testid="inventory-page">
      <Toolbar>
        <p className="text-sm text-muted-foreground">{list.length} produit(s) référencé(s)</p>
        <Btn onClick={openNew} data-testid="new-product-btn"><Plus size={16} /> Nouveau produit</Btn>
      </Toolbar>

      {list.length === 0 ? (
        <EmptyState icon={Boxes} title="Aucun produit en stock" sub="Ajoutez vos produits pour suivre les quantités et recevoir des alertes de réapprovisionnement." action={<Btn onClick={openNew}><Plus size={16} /> Ajouter</Btn>} />
      ) : (
        <div className="border border-border bg-card">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
                <th className="px-4 py-3 font-medium">Produit</th>
                <th className="px-4 py-3 font-medium">SKU</th>
                <th className="px-4 py-3 font-medium">Catégorie</th>
                <th className="px-4 py-3 text-right font-medium">Prix</th>
                <th className="px-4 py-3 text-right font-medium">Stock</th>
                <th className="px-4 py-3 text-right font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {list.map((p) => {
                const low = p.quantity <= p.reorder_level;
                return (
                  <tr key={p.id} className="border-b border-border transition-colors last:border-0 hover:bg-accent/40" data-testid={`product-row-${p.id}`}>
                    <td className="px-4 py-3 font-medium">{p.name}</td>
                    <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{p.sku || "—"}</td>
                    <td className="px-4 py-3 text-muted-foreground">{p.category || "—"}</td>
                    <td className="px-4 py-3 text-right font-mono">{euro(p.price)}</td>
                    <td className="px-4 py-3 text-right">
                      <span className={`inline-flex items-center gap-1 font-mono ${low ? "text-destructive" : ""}`}>
                        {low && <AlertTriangle size={14} />}
                        {p.quantity} {p.unit}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => openEdit(p)} data-testid={`edit-product-${p.id}`} className="p-2 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"><Pencil size={16} /></button>
                        {user?.role === "admin" && (
                          <button onClick={() => del(p.id)} data-testid={`delete-product-${p.id}`} className="p-2 text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive"><Trash2 size={16} /></button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title={editId ? "Modifier le produit" : "Nouveau produit"}>
        <form onSubmit={save} className="space-y-4">
          <Field label="Nom"><input required className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="product-name" /></Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="SKU"><input className={inputCls} value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} data-testid="product-sku" /></Field>
            <Field label="Catégorie"><input className={inputCls} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="product-category" /></Field>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Prix de vente (€)"><input type="number" step="0.01" className={inputCls} value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="product-price" /></Field>
            <Field label="Coût (€)"><input type="number" step="0.01" className={inputCls} value={form.cost} onChange={(e) => setForm({ ...form, cost: e.target.value })} data-testid="product-cost" /></Field>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <Field label="Quantité"><input type="number" className={inputCls} value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} data-testid="product-qty" /></Field>
            <Field label="Seuil d'alerte"><input type="number" className={inputCls} value={form.reorder_level} onChange={(e) => setForm({ ...form, reorder_level: e.target.value })} data-testid="product-reorder" /></Field>
            <Field label="Unité"><input className={inputCls} value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} data-testid="product-unit" /></Field>
          </div>
          <div className="flex justify-end gap-2 border-t border-border pt-4">
            <Btn type="button" variant="secondary" onClick={() => setOpen(false)}>Annuler</Btn>
            <Btn type="submit" loading={saving} data-testid="save-product-btn">Enregistrer</Btn>
          </div>
        </form>
      </Modal>
    </div>
  );
}
